"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import { getAuth } from "firebase/auth";
import {
  getMessaging,
  getToken,
  onMessage,
  isSupported,
} from "firebase/messaging";
import { db } from "@/firebase/config";
import { doc, getDoc, setDoc, serverTimestamp } from "@/lib/firestore-instrumented";
import {
  detectPushPlatform,
  isStandaloneWebApp,
  PUSH_STATUS,
  resolvePushStatus,
} from "@/lib/pushSupport.mjs";

function isInstalledPWA() {
  try {
    if (typeof window === "undefined") return false;
    return isStandaloneWebApp({
      displayModeStandalone: Boolean(
        window.matchMedia && window.matchMedia("(display-mode: standalone)").matches
      ),
      navigatorStandalone: Boolean(window.navigator?.standalone),
    });
  } catch {
    return false;
  }
}

function detectPlatform() {
  if (typeof navigator === "undefined") return "web";
  return detectPushPlatform({
    userAgent: navigator.userAgent || "",
    maxTouchPoints: navigator.maxTouchPoints || 0,
  });
}

function getClientInstanceId() {
  const storageKey = "sproutly-push-client-instance";
  try {
    const existing = window.localStorage.getItem(storageKey);
    if (existing) return existing;
    const id =
      typeof window.crypto?.randomUUID === "function"
        ? window.crypto.randomUUID()
        : `web-${Date.now()}-${Math.random().toString(36).slice(2)}`;
    window.localStorage.setItem(storageKey, id);
    return id;
  } catch {
    return "";
  }
}

/**
 * Registers a previously authorized user for web push and exposes a user-driven
 * enable action. Permission prompts stay inside enable() so iOS preserves the
 * initiating tap's user activation.
 */
export function useRegisterPush({
  user,
  vapidKey,
  onForegroundMessage,
  disabled = false,
  surface = null,
} = {}) {
  const [ready, setReady] = useState(false);
  const [error, setError] = useState(null);
  const [status, setStatus] = useState(PUSH_STATUS.CHECKING);
  const [platform, setPlatform] = useState("web");
  const [installed, setInstalled] = useState(false);
  const unsubscribeRef = useRef(null);

  const clearForegroundListener = useCallback(() => {
    if (typeof unsubscribeRef.current !== "function") return;
    try {
      unsubscribeRef.current();
    } catch {}
    unsubscribeRef.current = null;
  }, []);

  const registerCurrentDevice = useCallback(async ({ isCurrent = () => true } = {}) => {
    const auth = getAuth();
    const uid = user?.uid ?? auth.currentUser?.uid;
    if (!uid || !vapidKey) throw new Error("Push registration is not ready.");

    const registration = await navigator.serviceWorker.register("/sw.js");
    await navigator.serviceWorker.ready;
    const messaging = getMessaging();
    const token = await getToken(messaging, {
      vapidKey,
      serviceWorkerRegistration: registration,
    });
    if (!token) throw new Error("This device did not return a push token.");
    if (!isCurrent()) return null;

    await setDoc(doc(db, "userPush", uid), { updatedAt: serverTimestamp() }, { merge: true });
    if (!isCurrent()) return null;
    const tokenRef = doc(db, "userPush/" + uid + "/webTokens/" + token);
    const tokenSnap = await getDoc(tokenRef);
    if (!isCurrent()) return null;
    await setDoc(
      tokenRef,
      {
        token,
        platform: detectPlatform(),
        provider: "webpush",
        firebaseProjectId: process.env.NEXT_PUBLIC_FIREBASE_PROJECT_ID || "",
        firebaseMessagingSenderId: process.env.NEXT_PUBLIC_FIREBASE_MESSAGING_SENDER_ID || "",
        firebaseAuthDomain: process.env.NEXT_PUBLIC_FIREBASE_AUTH_DOMAIN || "",
        surfaceOrigin: surface?.origin || window.location.origin,
        portalId: surface?.portalId || null,
        portalRevisionId: surface?.revisionId || null,
        notificationIconPath: surface?.notificationIconPath || null,
        surfaceType: surface?.portalId ? "publisher-portal" : "sproutly-web",
        clientInstanceId: getClientInstanceId(),
        ua: navigator.userAgent,
        ...(tokenSnap.exists() ? {} : { createdAt: serverTimestamp() }),
        lastSeenAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      },
      { merge: true }
    );
    if (!isCurrent()) return null;

    clearForegroundListener();
    unsubscribeRef.current = typeof onForegroundMessage === "function"
      ? onMessage(messaging, onForegroundMessage)
      : null;
    return token;
  }, [clearForegroundListener, onForegroundMessage, surface, user?.uid, vapidKey]);

  useEffect(() => {
    setReady(false);
    setError(null);

    const auth = getAuth();
    const uid = user?.uid ?? auth.currentUser?.uid;
    let cancelled = false;

    (async () => {
      try {
        const currentPlatform = detectPlatform();
        const currentInstalled = isInstalledPWA();
        const hasNotificationApi = typeof window !== "undefined" && "Notification" in window;
        const hasServiceWorker = typeof navigator !== "undefined" && "serviceWorker" in navigator;
        setPlatform(currentPlatform);
        setInstalled(currentInstalled);

        if (disabled || !uid || !vapidKey || !hasNotificationApi || !hasServiceWorker) {
          clearForegroundListener();
          setStatus(resolvePushStatus({
            disabled,
            signedIn: Boolean(uid),
            hasVapidKey: Boolean(vapidKey),
            hasNotificationApi,
            hasServiceWorker,
            supported: false,
            platform: currentPlatform,
            installed: currentInstalled,
          }));
          return;
        }

        const supported = await isSupported();
        if (cancelled) return;
        const nextStatus = resolvePushStatus({
          signedIn: true,
          hasVapidKey: true,
          hasNotificationApi: true,
          hasServiceWorker: true,
          supported,
          platform: currentPlatform,
          installed: currentInstalled,
          permission: Notification.permission,
        });
        if (nextStatus !== PUSH_STATUS.ENABLED) {
          setStatus(nextStatus);
          return;
        }

        setStatus(PUSH_STATUS.ENABLING);
        const token = await registerCurrentDevice({ isCurrent: () => !cancelled });
        if (cancelled) return;
        if (!token) return;
        setReady(true);
        setStatus(PUSH_STATUS.ENABLED);
      } catch (err) {
        if (!cancelled) {
          setError(err);
          setStatus(PUSH_STATUS.ERROR);
          console.warn("[push] register failed", err);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [clearForegroundListener, disabled, registerCurrentDevice, user?.uid, vapidKey]);

  useEffect(() => () => clearForegroundListener(), [clearForegroundListener]);

  const enable = useCallback(async () => {
    setError(null);
    const currentPlatform = detectPlatform();
    const currentInstalled = isInstalledPWA();
    setPlatform(currentPlatform);
    setInstalled(currentInstalled);

    if (currentPlatform === "ios" && !currentInstalled) {
      setStatus(PUSH_STATUS.INSTALL_REQUIRED);
      return false;
    }
    if (
      typeof window === "undefined" ||
      !("Notification" in window) ||
      !("serviceWorker" in navigator)
    ) {
      setStatus(PUSH_STATUS.UNSUPPORTED);
      return false;
    }

    try {
      let permission = Notification.permission;
      if (permission === "default") {
        const permissionRequest = Notification.requestPermission();
        setStatus(PUSH_STATUS.ENABLING);
        permission = await permissionRequest;
      } else {
        setStatus(PUSH_STATUS.ENABLING);
      }

      if (permission === "denied") {
        setStatus(PUSH_STATUS.BLOCKED);
        return false;
      }
      if (permission !== "granted") {
        setStatus(PUSH_STATUS.PROMPT);
        return false;
      }

      await registerCurrentDevice();
      setReady(true);
      setStatus(PUSH_STATUS.ENABLED);
      return true;
    } catch (err) {
      setError(err);
      setStatus(PUSH_STATUS.ERROR);
      console.warn("[push] enable failed", err);
      return false;
    }
  }, [registerCurrentDevice]);

  return { ready, error, status, platform, installed, enable };
}
